import {
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CInput,
  CInputGroup,
  CInputGroupText,
  CLabel,
  CPagination,
  CRow,
  CTooltip,
} from "@coreui/react";
import { useEffect, useState } from "react";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";

import { getList } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { warningAlert } from "../utils/messages";
import { formatDateTime } from "../../utils/dates";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import { defaultValueOnUndefined } from "../../utils/fields";
import SingleStoreSelect from "../store/SingleStoreSelect";
import Store from "../../models/store";
import StoreExchange, {
  getExchangeDifferenceColor,
  getExchangeDifferenceLabel,
} from "../../models/store-exchange";
import StoreExchangeDetailModal from "./StoreExchangeDetailModal";

const ITEMS_PER_PAGE = 30;

const StoreExchangeCrud = () => {
  const fields = [
    { key: "id", _classes: ["text-center"], label: "Nro de Cambio" },
    { key: "originalSaleId", _classes: ["text-center"], label: "Venta" },
    { key: "exchangeDate", _classes: ["text-center"], label: "Fecha" },
    { key: "storeName", _classes: ["text-center"], label: "Sucursal" },
    { key: "sellerName", _classes: ["text-center"], label: "Usuario" },
    { key: "clientName", _classes: ["text-center"], label: "Cliente" },
    { key: "returnedTotal", _classes: ["text-center"], label: "Total devuelto" },
    {
      key: "deliveredTotal",
      _classes: ["text-center"],
      label: "Total entregado",
    },
    {
      key: "priceDifference",
      _classes: ["text-center"],
      label: "Diferencia",
    },
    { key: "reverted", _classes: ["text-center"], label: "Estado" },
    { key: "actions", _classes: ["text-center"], label: "Acciones", filter: false },
  ];

  const canRevert = true;

  const [exchanges, setExchanges] = useState<StoreExchange[]>([]);
  const [filterStore, setFilterStore] = useState<Store | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | moment.Moment>("");
  const [endDate, setEndDate] = useState<string | moment.Moment>("");
  const [search, setSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState<
    StoreExchange | undefined
  >(undefined);

  const getURLParams = (): Map<string, string> => {
    const map = new Map<string, string>();
    if (moment.isMoment(startDate)) {
      map.set("start_date", startDate.toISOString());
    }
    if (moment.isMoment(endDate)) {
      map.set("end_date", endDate.toISOString());
    }
    if (filterStore !== undefined && filterStore.id !== undefined) {
      map.set("store_id", filterStore.id.toString());
    }
    if (search !== "") {
      map.set("search", search);
    }
    return map;
  };

  const fetchExchanges = async () => {
    setLoading(true);
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = Math.max(0, ITEMS_PER_PAGE * (currentPage - 1));
    const status = await getList<StoreExchange>(
      "/store_exchanges/",
      limit,
      offset,
      urlParams
    );
    if (status.status === SUCCESS && status.data !== undefined) {
      const count = status.data.count;
      setTotalPages(Math.ceil((count ? count : 0) / ITEMS_PER_PAGE));
      setExchanges(status.data.items);
    } else {
      warningAlert(status.detail ? status.detail : "Error desconocido");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchExchanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const onFilter = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchExchanges();
    }
  };

  const onDetailClick = (exchange: StoreExchange) => {
    setSelectedExchange(exchange);
    setShowDetailModal(true);
  };

  const onDetailCancel = () => {
    setShowDetailModal(false);
    setSelectedExchange(undefined);
  };

  const onReverted = async () => {
    setShowDetailModal(false);
    setSelectedExchange(undefined);
    await fetchExchanges();
  };

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader>
              <h4>Cambios realizados</h4>
            </CCardHeader>
            <CCardBody>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Desde:</CLabel>
                </CCol>
                <CCol>
                  <Datetime
                    className="pl-0"
                    onChange={(value) => setStartDate(value)}
                    value={startDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                </CCol>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Hasta:</CLabel>
                </CCol>
                <CCol>
                  <Datetime
                    className="pl-0"
                    onChange={(value) => setEndDate(value)}
                    value={endDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                </CCol>
              </CRow>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Sucursal:</CLabel>
                </CCol>
                <CCol>
                  <SingleStoreSelect
                    value={filterStore ? filterStore : null}
                    onChange={(newStore: Store | null) =>
                      setFilterStore(newStore !== null ? newStore : undefined)
                    }
                  ></SingleStoreSelect>
                </CCol>
              </CRow>
              <CRow className={"mt-2"}>
                <CCol>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Buscar (cliente / nro de venta)"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearch(e.target.value)
                      }
                      value={search}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          onFilter();
                        }
                      }}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onFilter}
                    >
                      Filtrar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div className="mt-2">
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={exchanges}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    id: (item: StoreExchange) => (
                      <td className="text-center">
                        {item.id ? `#${item.id}` : "-"}
                      </td>
                    ),
                    originalSaleId: (item: StoreExchange) => (
                      <td className="text-center">
                        {item.originalSaleId ? `#${item.originalSaleId}` : "-"}
                      </td>
                    ),
                    exchangeDate: (item: StoreExchange) => (
                      <td className="text-center">
                        {item.exchangeDate
                          ? formatDateTime(new Date(item.exchangeDate))
                          : "-"}
                      </td>
                    ),
                    clientName: (item: StoreExchange) => (
                      <td className="text-center">
                        {defaultValueOnUndefined("SIN NOMBRE", item.clientName)}
                      </td>
                    ),
                    returnedTotal: (item: StoreExchange) => (
                      <td className="text-center">
                        {item.returnedTotal !== undefined
                          ? formatToCurrency(item.returnedTotal, PYG)
                          : "-"}
                      </td>
                    ),
                    deliveredTotal: (item: StoreExchange) => (
                      <td className="text-center">
                        {item.deliveredTotal !== undefined
                          ? formatToCurrency(item.deliveredTotal, PYG)
                          : "-"}
                      </td>
                    ),
                    priceDifference: (item: StoreExchange) => (
                      <td className="text-center">
                        {item.priceDifference !== undefined ? (
                          <CBadge
                            color={getExchangeDifferenceColor(
                              item.priceDifference
                            )}
                          >
                            {getExchangeDifferenceLabel(item.priceDifference)}
                            {item.priceDifference !== 0
                              ? ` · ${formatToCurrency(
                                  Math.abs(item.priceDifference),
                                  PYG
                                )}`
                              : ""}
                          </CBadge>
                        ) : (
                          "-"
                        )}
                      </td>
                    ),
                    reverted: (item: StoreExchange) => (
                      <td className="text-center">
                        {item.reverted ? (
                          <CBadge color={"danger"}>Revertido</CBadge>
                        ) : (
                          <CBadge color={"success"}>Completado</CBadge>
                        )}
                      </td>
                    ),
                    actions: (item: StoreExchange) => (
                      <td className="text-center">
                        <CButtonGroup>
                          <CTooltip content={"Ver detalle"}>
                            <CButton
                              className="text-white"
                              color="primary"
                              onClick={() => onDetailClick(item)}
                            >
                              <i className="fa fa-eye"></i>
                            </CButton>
                          </CTooltip>
                        </CButtonGroup>
                      </td>
                    ),
                  }}
                />
              </div>
              <div className="d-flex justify-content-center">
                <CPagination
                  pages={totalPages}
                  activePage={currentPage}
                  onActivePageChange={(i: number) =>
                    setCurrentPage(i < 1 ? 1 : i)
                  }
                  className={totalPages < 2 ? "d-none" : "mt-2"}
                />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <StoreExchangeDetailModal
        show={showDetailModal}
        storeExchange={selectedExchange}
        canRevert={canRevert}
        onCancel={onDetailCancel}
        onReverted={onReverted}
      ></StoreExchangeDetailModal>
    </>
  );
};

export default StoreExchangeCrud;
